import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useFormik } from "formik";
import queryString from "query-string";
import { LockIcon, MailIcon } from "vega-icons";
import * as Yup from "yup";

import { tenantConfig } from "@tenant-config";
import { useAuth, useSession } from "@vega-api/custom";
import { getCurrentPortalType } from "@vega-api/extended/auth/redirectUtils";
import {
  FeatureFlag,
  useNoUserFeatureFlag,
} from "@vega-api/extended/auth/useFeatureFlag";
import { FrontendPortal } from "@vega-api/generated/types/frontendPortal";
import { type LoginUserRequest } from "@vega-api/generated/types/loginUserRequest";
import { useGetProfile } from "@vega-api/generated/user-management-v1/user-management-v1";
import { ThemedButton } from "@vega-core/components/Buttons/ThemedButton";
import { ActionStatusMessage } from "@vega-core/components/Deprecated/ActionStatusMessage/ActionStatusMessage";
import { InputControl } from "@vega-core/components/Form/Controls/Input/InputControl";
import { PasswordInput } from "@vega-core/components/Form/Controls/Password/PasswordInput";
import { NoticeType } from "@vega-core/components/Notice/Notice";
import { usePublicConfig } from "@vega-core/hooks/usePublicConfig";
import { m } from "@vega-core/paraglide/messages";
import {
  isF2F,
  isLocalSession,
  isLPCoInvestPortal,
  isMSW,
} from "@vega-core/utils/global";
import { isTruthy } from "@vega-core/utils/isTruthy";
import {
  trackFirstLoginSucceeded,
  trackLoginFailed,
} from "@vega-core/utils/posthog/onboardingAnalytics";
import { BffLoginButton } from "@vega-modules/components/BffLoginButton/BffLoginButton";
import { OktaLoginButton } from "@vega-modules/components/OktaLoginButton/OktaLoginButton";
import { LocalSessionForm } from "@vega-modules/scenes/LocalLogin/LocalSessionForm";

import { LoginFormError } from "./LoginFormError";

type LoginState = {
  email: string;
  password: string;
};

type Props = {
  heading?: string;
};

export const LPLoginForm = ({ heading }: Props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, loginError, logout } = useAuth();
  const session = useSession();
  const isBffAuthEnabled = useNoUserFeatureFlag(FeatureFlag.SPIKE_NEW_AUTH);

  // BFF authentication: try to fetch profile with SESSION cookie
  // If successful, user is authenticated and we redirect to home
  const { data: profileData, isSuccess: isProfileLoaded } = useGetProfile({
    query: {
      enabled: isBffAuthEnabled && !session.user, // Only check if BFF is enabled and we don't already have a user
      retry: false, // Don't retry on 401 - just means not authenticated
    },
    // withCredentials will be set automatically by authInterceptor if SESSION cookie exists
  });

  useEffect(() => {
    if (isProfileLoaded && profileData.data && !session.user) {
      session.setUserData(profileData.data);
      trackFirstLoginSucceeded({ userId: profileData.data.id });
      navigate("/");
    }
  }, [isProfileLoaded, profileData, session, navigate]);

  const { error: urlError } = queryString.parse(location.search) as {
    error?: string;
  };

  useEffect(() => {
    if (isTruthy(urlError)) {
      trackLoginFailed({ error: urlError });
    }
  }, [urlError]);

  const [routerError, setRouterError] = useState("");

  useEffect(() => {
    const stateError = location.state?.error;

    if (stateError && !routerError) {
      setRouterError(stateError);
      window.history.replaceState({}, "", location.pathname);
    }
  }, [location.state?.error, routerError, location.pathname]);

  const {
    global: { companyName },
  } = usePublicConfig();
  const useOktaLogin = tenantConfig.features.auth_okta_login_flow;

  if (session.user) {
    logout({
      isGlobal: false,
      savePath: false,
      source: FrontendPortal.LP_PORTAL,
    });
  }

  const [state] = useState<LoginState>({
    email: "",
    password: "",
  });

  const formik = useFormik({
    initialValues: state,
    onSubmit(values: LoginUserRequest, { setSubmitting, setTouched }) {
      login(values, () => {
        setSubmitting(false);
        setTouched({ email: false, password: false });
      });
    },
    validateOnBlur: true,
    validateOnChange: true,
    validationSchema: Yup.object().shape({
      email: Yup.string()
        .email(m.formValidation_invalid({ name: m.formFields_email() }))
        .required(
          m.formValidation_required({
            name: m.formFields_email(),
          }),
        ),
      password: Yup.string().required(
        m.formValidation_required({
          name: m.formFields_password(),
        }),
      ),
    }),
  });

  const statusMessage = useMemo(() => {
    // Check React Router state error first
    if (routerError) {
      return routerError;
    }

    // Fallback to URL parameter error for backward compatibility
    if (isTruthy(urlError)) {
      try {
        const decodedError = decodeURIComponent(urlError);

        return decodedError || m.errors_loginGenericError();
      } catch {
        return m.errors_loginGenericError();
      }
    }

    return "";
  }, [urlError, routerError]);

  const localSessionPortal = isLPCoInvestPortal
    ? FrontendPortal.COINVEST_INVESTOR_PORTAL
    : FrontendPortal.LP_PORTAL;

  let loginFormFields: React.ReactNode = null;

  if (isLocalSession && !isMSW && !isF2F) {
    loginFormFields = <LocalSessionForm portal={localSessionPortal} />;
  } else if (isMSW || isF2F) {
    loginFormFields = (
      <>
        <div className="border-b-1 border-border/primary w-full h-0.5" />
        <form onSubmit={formik.handleSubmit}>
          <div className="mt-1">
            <InputControl
              background="bg-background/secondary md:bg-background/primary"
              color="text-text/primary"
              error={
                formik.errors.email && formik.touched.email
                  ? formik.errors.email
                  : ""
              }
              icon={<MailIcon className="fill-text/primary" />}
              label={m.formFields_email()}
              name="email"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              placeholder=""
              type="email"
              value={formik.values.email}
              withBorder
            />
          </div>
          <div className="mt-6">
            <PasswordInput
              autocomplete="current-password"
              background="bg-background/secondary md:bg-background/primary"
              error={
                formik.errors.password && formik.touched.password
                  ? formik.errors.password
                  : ""
              }
              icon={<LockIcon className="w-5 h-auto fill-text/primary" />}
              label={m.formFields_password()}
              name="password"
              onBlur={formik.handleBlur}
              onChange={formik.handleChange}
              value={formik.values.password || ""}
              withBorder
            />
          </div>

          <div className="mt-10">
            <ThemedButton
              data-analytics="gtm-auth-login-success"
              dataTestId="continue-button"
              fullWidth
              variant="primary"
            >
              {m.btn_continue()}
            </ThemedButton>
          </div>
          <div className="mt-6">
            {loginError ? <LoginFormError loginError={loginError} /> : null}
          </div>
        </form>
      </>
    );
  }

  return (
    <div className="flex flex-col h-full justify-between w-full text-text/auth/primary">
      <div className="flex flex-col flex-grow justify-center gap-8">
        <div className="space-y-2">
          <h2 className="mt-6 whitespace-nowrap heading-5 md:heading-4">
            {heading ?? m.general_loginHeaderLP({ companyName })}
          </h2>
          <div className="body-base-regular">
            {m.auth_loginWelcomeMessage()}
          </div>
        </div>
        {loginFormFields}
        <div className="space-y-4">
          {isBffAuthEnabled || !useOktaLogin ? (
            <BffLoginButton source={getCurrentPortalType()} />
          ) : null}
          {useOktaLogin ? (
            <OktaLoginButton source={getCurrentPortalType()} />
          ) : null}
        </div>
      </div>

      {isTruthy(statusMessage) ? (
        <div className="mt-auto">
          <ActionStatusMessage
            message={statusMessage}
            type={NoticeType.ERROR}
            withContact
          />
        </div>
      ) : null}
    </div>
  );
};
